'use client';

import React, { useCallback } from 'react';
import Image from 'next/image';
import noResultsImage from '@cvent/carina-glyphs/illustrations/hero-search.svg';
import { formatMessage } from '@/utils/translationHelper';
import type { Locale } from '@/i18n';
import { Filter } from '@/models/Filter';
import { CSN_SEARCH_BANNER_COOKIE_NAME } from '@/constants/cookieConstants';
import { FILTERS_TO_RESET } from '@/constants/filterConstants';
import { useApplyFilter } from '@/hooks/useApplyFilter';
import { usePathname } from 'next/navigation';
import Button from '@/components/shared/button/Button';

type Props = {
  locale: Locale;
  noResultsPageStrings: {
    _do_not_see_a_supplier: string;
    _request_to_add_a_venue_to_cvent_supplier_netwrok: string;
    _send_a_request: string;
    _reset_filters: string;
    _csn_search_srp_no_results_page_expand_search_criteria_message: string;
    _csn_search_srp_no_results_page_no_results_found_message: string;
  };
  appliedFilters: Filter;
  isLoggedIn: boolean;
  rfpId: string | undefined;
  isLoginBannerDismissed: boolean;
};

export default function EmptyResults({
  locale,
  noResultsPageStrings,
  appliedFilters,
  isLoggedIn,
  rfpId,
  isLoginBannerDismissed
}: Props) {
  const { applyFilters } = useApplyFilter();
  const pathName = usePathname();
  const finalPath = pathName.split('/results/')[1];

  const onReset = useCallback(async () => {
    const updatedFilters = { ...appliedFilters };
    FILTERS_TO_RESET.forEach(filter => {
      delete updatedFilters[filter];
    });
    if (isLoggedIn && rfpId && document) {
      document.cookie = `${CSN_SEARCH_BANNER_COOKIE_NAME}=${JSON.stringify({
        isLoginBannerDismissed,
        dismissedRfpBannerId: rfpId
      })}`;
    }
    await applyFilters(finalPath, updatedFilters, true, 'CVENT_PICKS');
  }, [appliedFilters, applyFilters, finalPath, isLoggedIn, isLoginBannerDismissed, rfpId]);

  return (
    <div className="flex flex-col items-center justify-center">
      <div className="flex flex-col items-center justify-center">
        <Image
          src={noResultsImage}
          width={400}
          height={400}
          alt={noResultsPageStrings._csn_search_srp_no_results_page_no_results_found_message}
          style={{ marginTop: '-20px' }}
        />
        <div>
          <h2 className="font-loose text-center text-d-sm leading-relaxed">
            {noResultsPageStrings._csn_search_srp_no_results_page_no_results_found_message}
          </h2>
          <h3 className="-mt-2 max-w-xl pt-4 text-center leading-relaxed text-neutral-60">
            {noResultsPageStrings._csn_search_srp_no_results_page_expand_search_criteria_message}
          </h3>
        </div>
        <div className="m-4">
          <Button
            text={noResultsPageStrings._reset_filters}
            aria-label={noResultsPageStrings._reset_filters}
            onClick={onReset}
          />
        </div>
      </div>
      <p className="items-center justify-center text-center lg:flex">
        {noResultsPageStrings._do_not_see_a_supplier}
        {formatMessage(noResultsPageStrings._request_to_add_a_venue_to_cvent_supplier_netwrok, locale, {
          sendARequest: (
            <a
              className="text-brand-60"
              data-cvent-id="send-a-request"
              href="https://www.cvent.com/RFP/NewSupplierRequest.aspx?vtt=1"
            >
              &nbsp;{`${noResultsPageStrings._send_a_request}`}&nbsp;
            </a>
          )
        })}
      </p>
    </div>
  );
}
